import apiClient from './client';

// What the tenant's plan includes — GET /api/tenant/entitlements. Every
// client reads this and hides or locks features from it, never from the
// plan name (shared-docs/PLAN-TIERS-AND-ENTITLEMENTS.md section 7).

/** One key per backend `Entitlement`, camel-cased the way the endpoint returns it. */
export type EntitlementKey =
  | 'aiPlanning'
  | 'voiceCapture'
  | 'strategicDrivers'
  | 'decisions'
  | 'delegation'
  | 'peopleHistory'
  | 'principalProfile'
  | 'capacityPlanning'
  | 'projects'
  | 'eaWorkspace'
  | 'transcriptProcessing'
  | 'imageAnalysis'
  | 'pdfExport'
  | 'webhooks'
  | 'sso'
  | 'advancedReporting'
  | 'tenantAiKey'
  | 'apiAccess'
  | 'crossExecutiveReporting'
  | 'customBranding'
  | 'dedicatedOnboarding'
  | 'prioritySupport';

export interface TenantSeats {
  maxExecutiveSeats: number;
  maxEaSeats: number;
  executivesUsed: number;
  easUsed: number;
}

/** This month's AI allowance. Absent until the backend starts sending it. */
export interface AiUsageSummary {
  operationsUsed: number;
  operationsAllowance: number;
  /** Where requests stop (2x the allowance). */
  hardStopAt: number;
  resetsAt: string;
  /** The tenant runs on its own AI key: usage is counted, never limited. */
  tenantOwnedKey: boolean;
  backgroundBriefsToday?: number;
  backgroundBriefsPerDay?: number;
}

export interface TenantEntitlements {
  plan: string;
  status: string;
  isTrial: boolean;
  trialExpiresAt?: string | null;
  billingInterval?: string | null;
  isSelfServeIndividual: boolean;
  seats: TenantSeats;
  entitlements: Partial<Record<EntitlementKey, boolean>>;
  /** The cheapest plan that includes each entitlement, for upgrade copy. */
  requiredPlans: Partial<Record<EntitlementKey, string>>;
  aiUsage?: AiUsageSummary | null;
}

export const entitlementsApi = {
  get: () => apiClient.get<TenantEntitlements>('/tenant/entitlements').then(r => r.data),
};

/** "StrategicDrivers" (the 403 body's spelling) to "strategicDrivers". */
export function entitlementKey(name: string | null | undefined): EntitlementKey | undefined {
  if (!name) return undefined;
  return (name.charAt(0).toLowerCase() + name.slice(1)) as EntitlementKey;
}

/** How a feature is named in upgrade copy, and whether the name is plural. */
export interface EntitlementLabel {
  label: string;
  /** "Decisions aren't on your plan", not "isn't". */
  plural?: boolean;
}

const plural = (label: string): EntitlementLabel => ({ label, plural: true });

export const ENTITLEMENT_LABELS: Record<EntitlementKey, EntitlementLabel> = {
  aiPlanning: { label: 'AI planning' },
  voiceCapture: { label: 'Voice capture' },
  strategicDrivers: plural('Strategic drivers'),
  decisions: plural('Decisions'),
  delegation: { label: 'Delegation' },
  peopleHistory: { label: 'People history' },
  principalProfile: plural('Principal profiles'),
  capacityPlanning: { label: 'Capacity planning' },
  projects: plural('Projects'),
  eaWorkspace: { label: 'The EA workspace' },
  transcriptProcessing: { label: 'Transcript processing' },
  imageAnalysis: { label: 'Image capture' },
  pdfExport: { label: 'Export' },
  webhooks: plural('Webhooks'),
  sso: { label: 'Single sign-on' },
  advancedReporting: { label: 'Advanced reporting' },
  tenantAiKey: { label: 'Your own AI key' },
  apiAccess: { label: 'API access' },
  crossExecutiveReporting: { label: 'Cross-executive reporting' },
  customBranding: { label: 'Custom branding' },
  dedicatedOnboarding: { label: 'Dedicated onboarding' },
  prioritySupport: { label: 'Priority support' },
};
